<script lang="ts">
  import { onDestroy } from 'svelte'
  import {
    forceSimulation,
    forceManyBody,
    forceLink,
    forceCenter,
    forceCollide,
    type Simulation,
    type SimulationNodeDatum,
    type SimulationLinkDatum,
  } from 'd3-force'
  import { select } from 'd3-selection'
  import { zoom, zoomIdentity } from 'd3-zoom'
  import { drag } from 'd3-drag'

  type GraphNodeData = {
    id: number
    kind: string
    name: string
    description: string | null
    mention_count: number
  }

  type GraphEdgeData = {
    source: number
    target: number
    kind: string
    weight: number
  }

  type GraphClusterResponse = {
    focal_id: number
    nodes: GraphNodeData[]
    edges: GraphEdgeData[]
    truncated: boolean
    total_neighbours: number
  }

  type SimNode = GraphNodeData & SimulationNodeDatum
  type SimEdge = SimulationLinkDatum<SimNode> & GraphEdgeData

  interface Props {
    entityId: number
    depth?: number
    width?: number
    height?: number
    onFocalChange?: (entityId: number) => void
    onClusterChange?: (nodeIds: number[]) => void
  }

  let { entityId, depth = 2, width = 900, height = 640, onFocalChange, onClusterChange }: Props = $props()

  let nodes = $state<SimNode[]>([])
  let edges = $state<SimEdge[]>([])
  let truncated = $state(false)
  let totalNeighbours = $state(0)
  let loading = $state(true)
  let error = $state<string | null>(null)

  let svgEl = $state<SVGSVGElement | undefined>(undefined)
  let simulation: Simulation<SimNode, SimEdge> | null = null
  let zoomTransform = $state({ k: 1, x: 0, y: 0 })

  const KIND_COLOR: Record<string, string> = {
    person: '#3b82f6',
    topic: '#10b981',
    event: '#ef4444',
    other: '#9ca3af',
    source: '#a855f7',
  }

  const EDGE_COLOR: Record<string, string> = {
    co_occurs: 'rgba(148, 163, 184, 0.6)',
    extracted_from: 'rgba(168, 85, 247, 0.7)',
    variant_of: 'rgba(248, 113, 113, 0.85)',
  }

  function nodeRadius(node: GraphNodeData): number {
    const base = 6
    const scale = Math.log2(node.mention_count + 1) * 2
    return base + scale
  }

  async function loadCluster() {
    loading = true
    error = null
    try {
      const response = await fetch(`/api/graph/cluster?entity_id=${entityId}&depth=${depth}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) {
        throw new Error(`graph cluster request failed: ${response.status}`)
      }
      const data: GraphClusterResponse = await response.json()
      truncated = data.truncated
      totalNeighbours = data.total_neighbours
      hydrate(data)
    } catch (err) {
      error = err instanceof Error ? err.message : String(err)
    } finally {
      loading = false
    }
  }

  function hydrate(data: GraphClusterResponse) {
    const previousById = new Map(nodes.map((node) => [node.id, node]))
    nodes = data.nodes.map((node) => {
      const previous = previousById.get(node.id)
      return {
        ...node,
        x: previous?.x ?? width / 2 + (Math.random() - 0.5) * 80,
        y: previous?.y ?? height / 2 + (Math.random() - 0.5) * 80,
        vx: previous?.vx ?? 0,
        vy: previous?.vy ?? 0,
      }
    })
    onClusterChange?.(data.nodes.map((node) => node.id))
    const byId = new Map(nodes.map((node) => [node.id, node]))
    edges = data.edges
      .map((edge) => {
        const source = byId.get(edge.source)
        const target = byId.get(edge.target)
        if (!source || !target) return null
        return { ...edge, source, target } as SimEdge
      })
      .filter((edge): edge is SimEdge => edge !== null)
    restartSimulation()
  }

  function restartSimulation() {
    simulation?.stop()
    simulation = forceSimulation<SimNode>(nodes)
      .force(
        'link',
        forceLink<SimNode, SimEdge>(edges)
          .id((node) => String(node.id))
          .distance((edge) => 80 / Math.log2(edge.weight + 2)),
      )
      .force('charge', forceManyBody().strength(-160))
      .force('center', forceCenter(width / 2, height / 2))
      .force('collide', forceCollide<SimNode>().radius((node) => nodeRadius(node) + 4))
      .alpha(1)
      .restart()
    simulation.on('tick', () => {
      nodes = [...nodes]
      edges = [...edges]
    })
  }

  let zoomAttached = false
  const dragBehavior = drag<SVGCircleElement, SimNode>()
    .on('start', (event, node) => {
      if (!event.active) simulation?.alphaTarget(0.3).restart()
      node.fx = node.x
      node.fy = node.y
    })
    .on('drag', (event, node) => {
      node.fx = event.x
      node.fy = event.y
    })
    .on('end', (event, node) => {
      if (!event.active) simulation?.alphaTarget(0)
      node.fx = null
      node.fy = null
    })

  function attachZoomOnce() {
    if (zoomAttached || !svgEl) return
    const zoomBehavior = zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.2, 4])
      .on('zoom', (event) => {
        zoomTransform = { k: event.transform.k, x: event.transform.x, y: event.transform.y }
      })
    const svg = select(svgEl)
    svg.call(zoomBehavior).call(zoomBehavior.transform, zoomIdentity)
    zoomAttached = true
  }

  function attachDrag() {
    if (!svgEl) return
    select(svgEl).selectAll<SVGCircleElement, SimNode>('circle.node').call(dragBehavior)
  }

  function handleNodeClick(node: SimNode) {
    if (node.id === entityId) return
    onFocalChange?.(node.id)
  }

  $effect(() => {
    void entityId
    void depth
    loadCluster()
  })

  $effect(() => {
    if (svgEl && nodes.length > 0) {
      attachZoomOnce()
      attachDrag()
    }
  })

  onDestroy(() => {
    simulation?.stop()
  })
</script>

<div class="graph-view-root" data-testid="graph-view">
  {#if loading}
    <div class="graph-status">Cluster laden...</div>
  {:else if error}
    <div class="graph-status graph-status-error">Kon graph niet laden: {error}</div>
  {:else if nodes.length === 0}
    <div class="graph-status">Geen buren gevonden voor deze entiteit.</div>
  {:else}
    {#if truncated}
      <div class="graph-truncated">
        Toont {nodes.length} van {totalNeighbours + 1} entiteiten; cluster is afgekapt op {nodes.length}.
      </div>
    {/if}
    <svg
      bind:this={svgEl}
      class="graph-svg"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      data-testid="graph-svg"
    >
      <g transform={`translate(${zoomTransform.x},${zoomTransform.y}) scale(${zoomTransform.k})`}>
        {#each edges as edge (`${edge.source.id}-${edge.target.id}-${edge.kind}`)}
          <line
            class="graph-edge"
            x1={edge.source.x}
            y1={edge.source.y}
            x2={edge.target.x}
            y2={edge.target.y}
            stroke={EDGE_COLOR[edge.kind] ?? '#94a3b8'}
            stroke-width={Math.min(1 + edge.weight / 2, 5)}
            stroke-dasharray={edge.kind === 'variant_of' ? '4 3' : '0'}
          />
        {/each}
        {#each nodes as node (node.id)}
          <g class="graph-node" transform={`translate(${node.x ?? 0},${node.y ?? 0})`}>
            <circle
              class="node"
              role="button"
              tabindex="0"
              aria-label={`Focus op ${node.name}`}
              r={nodeRadius(node)}
              fill={KIND_COLOR[node.kind] ?? '#9ca3af'}
              stroke={node.id === entityId ? '#fde047' : 'rgba(15,23,42,0.4)'}
              stroke-width={node.id === entityId ? 3 : 1}
              onclick={() => handleNodeClick(node)}
              onkeydown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  handleNodeClick(node)
                }
              }}
              data-testid={`graph-node-${node.id}`}
            />
            <title>{node.name}{node.description ? `: ${node.description}` : ''}</title>
            <text
              class="node-label"
              dy="-10"
              text-anchor="middle"
              font-size="11"
              fill="#0f172a"
              pointer-events="none"
            >
              {node.name}
            </text>
          </g>
        {/each}
      </g>
    </svg>
  {/if}
</div>

<style>
  .graph-view-root {
    width: 100%;
    height: 100%;
    position: relative;
  }
  .graph-svg {
    width: 100%;
    height: 100%;
    background: #f8fafc;
    border-radius: 0.5rem;
  }
  .graph-edge {
    pointer-events: none;
  }
  .graph-node .node {
    cursor: pointer;
  }
  .graph-node .node:hover {
    stroke: #fde047;
    stroke-width: 2;
  }
  .graph-status {
    padding: 1.5rem;
    color: #64748b;
    text-align: center;
  }
  .graph-status-error {
    color: #b91c1c;
  }
  .graph-truncated {
    position: absolute;
    top: 0.5rem;
    right: 0.75rem;
    padding: 0.25rem 0.75rem;
    background: rgba(248, 250, 252, 0.95);
    border: 1px solid #e2e8f0;
    border-radius: 9999px;
    font-size: 0.75rem;
    color: #475569;
  }
</style>
